import { ProfileData } from '../types/profile.types';
// Función para validar URL
const validateUrl = (url: string): boolean => {
  if (url.trim() === '') return false;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return urlRegex.test(url);
  }
  const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
  return domainRegex.test(url);
};
// Función para validar teléfono según el país
const validatePhoneNumber = (countryCode: string, phoneNumber: string): boolean => {
  if (!phoneNumber || !phoneNumber.trim()) return false;
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  const patterns: { [key: string]: RegExp } = {
    'AR': /^\d{10,11}$/, 'BR': /^\d{10,11}$/, 'CL': /^\d{8,9}$/, 'CO': /^\d{10}$/,
    'MX': /^\d{10}$/, 'PE': /^\d{9}$/, 'UY': /^\d{8,9}$/, 'VE': /^\d{10,11}$/,
    'BO': /^\d{8}$/, 'EC': /^\d{9}$/, 'PY': /^\d{9}$/, 'CR': /^\d{8}$/,
    'PA': /^\d{8}$/, 'GT': /^\d{8}$/, 'HN': /^\d{8}$/, 'SV': /^\d{8}$/,
    'NI': /^\d{8}$/, 'CU': /^\d{8}$/, 'DO': /^\d{10}$/, 'HT': /^\d{8}$/,
    'JM': /^\d{10}$/, 'TT': /^\d{10}$/, 'US': /^\d{10}$/, 'CA': /^\d{10}$/,
    'ES': /^\d{9}$/, 'IT': /^\d{10,11}$/, 'FR': /^\d{10}$/, 'DE': /^\d{10,11}$/,
    'GB': /^\d{10,11}$/, 'PT': /^\d{9}$/, 'NL': /^\d{9}$/, 'BE': /^\d{9}$/,
    'CH': /^\d{9}$/, 'AT': /^\d{10,11}$/, 'SE': /^\d{9}$/, 'NO': /^\d{8}$/,
    'DK': /^\d{8}$/, 'FI': /^\d{9,10}$/, 'PL': /^\d{9}$/, 'CZ': /^\d{9}$/,
    'HU': /^\d{9}$/, 'RO': /^\d{10}$/, 'BG': /^\d{9}$/, 'GR': /^\d{10}$/,
    'RU': /^\d{10,11}$/, 'UA': /^\d{9}$/, 'IE': /^\d{9}$/, 'IS': /^\d{7}$/,
    'CN': /^\d{11}$/, 'JP': /^\d{10,11}$/, 'KR': /^\d{10,11}$/, 'IN': /^\d{10}$/,
    'TH': /^\d{9}$/, 'SG': /^\d{8}$/, 'MY': /^\d{9,10}$/, 'ID': /^\d{10,12}$/,
    'PH': /^\d{10}$/, 'VN': /^\d{9,10}$/, 'TW': /^\d{9}$/, 'HK': /^\d{8}$/,
    'BD': /^\d{10}$/, 'PK': /^\d{10}$/, 'LK': /^\d{9}$/, 'NP': /^\d{10}$/,
    'MM': /^\d{9,10}$/, 'KH': /^\d{8,9}$/, 'LA': /^\d{8,9}$/, 'MN': /^\d{8}$/,
    'AU': /^\d{9,10}$/, 'NZ': /^\d{8,9}$/, 'FJ': /^\d{7,8}$/, 'PG': /^\d{8}$/,
    'NC': /^\d{6}$/, 'ZA': /^\d{9}$/, 'EG': /^\d{10}$/, 'NG': /^\d{10,11}$/,
    'KE': /^\d{9,10}$/, 'MA': /^\d{9}$/, 'TN': /^\d{8}$/, 'DZ': /^\d{9}$/,
    'GH': /^\d{9}$/, 'ET': /^\d{9}$/, 'UG': /^\d{9}$/, 'TZ': /^\d{9}$/,
    'ZW': /^\d{9}$/, 'BW': /^\d{7,8}$/, 'NA': /^\d{9}$/, 'ZM': /^\d{9}$/,
    'MW': /^\d{9}$/, 'MZ': /^\d{9}$/, 'MG': /^\d{9}$/, 'MU': /^\d{7,8}$/,
    'SC': /^\d{7}$/, 'SA': /^\d{9}$/, 'AE': /^\d{9}$/, 'QA': /^\d{8}$/,
    'KW': /^\d{8}$/, 'BH': /^\d{8}$/, 'OM': /^\d{8}$/, 'JO': /^\d{9}$/,
    'LB': /^\d{8}$/, 'SY': /^\d{9}$/, 'IQ': /^\d{10}$/, 'IR': /^\d{10}$/,
    'IL': /^\d{9}$/, 'TR': /^\d{10}$/
  };
  const pattern = patterns[countryCode];
  return pattern ? pattern.test(cleanNumber) : cleanNumber.length >= 7 && cleanNumber.length <= 15;
};
/**
 * Calcula el progreso del perfil y determina si está completo
 * @param profileData Datos del perfil
 * @param wantsToManageProfile Si el usuario quiere administrar el perfil institucional
 * @returns Objeto con percentage (0-100) e isComplete (true si es 100%)
 */
export const calculateProfileProgress = (
  profileData: ProfileData,
  wantsToManageProfile: boolean
): { percentage: number; isComplete: boolean } => {
  let completedFields = 0;
  let totalFields = 0;
  // Normalizar arrays de temáticas
  const personalThematicAxesRaw = profileData.contactInfo?.thematicAxes;
  const orgThematicAxesRaw = profileData.basicInfo?.thematicAxes;
  const personalThematicAxesNormalized = Array.isArray(personalThematicAxesRaw) 
    ? personalThematicAxesRaw.filter((axis: any) => {
        const axisStr = String(axis);
        return axisStr && 
          axisStr.trim() !== '' && 
          axisStr !== '[' && 
          axisStr !== ']' &&
          !axisStr.startsWith('{') &&
          !axisStr.startsWith('"[');
      })
    : [];
  const orgThematicAxesNormalized = Array.isArray(orgThematicAxesRaw) 
    ? orgThematicAxesRaw.filter((axis: any) => {
        const axisStr = String(axis);
        return axisStr && 
          axisStr.trim() !== '' && 
          axisStr !== '[' && 
          axisStr !== ']' &&
          !axisStr.startsWith('{') &&
          !axisStr.startsWith('"[');
      })
    : [];
  // PERFIL PERSONAL: solo contar campos de Info Personal (11 campos)
  if (!wantsToManageProfile) {
    // 1. Nombre
    totalFields++;
    if (profileData.contactInfo.firstName && profileData.contactInfo.firstName.toString().trim() !== '') {
      completedFields++;
    }
    // 2. Apellido
    totalFields++;
    if (profileData.contactInfo.lastName && profileData.contactInfo.lastName.toString().trim() !== '') {
      completedFields++;
    }
    // 3. Correo electrónico
    totalFields++;
    const email = profileData.contactInfo.email || '';
    if (email && email.toString().trim() !== '' && email.includes('@')) {
      completedFields++;
    }
    // 4. Teléfono
    totalFields++;
    const codigoPais = profileData.contactInfo.codigoPais || 'AR';
    const numeroTelefono = profileData.contactInfo.numeroTelefono || '';
    if (numeroTelefono && numeroTelefono.toString().trim() !== '' && validatePhoneNumber(codigoPais, numeroTelefono.toString().trim())) {
      completedFields++;
    }
    // 5. Organización
    totalFields++;
    if (profileData.basicInfo.organizationName && profileData.basicInfo.organizationName.toString().trim() !== '') {
      completedFields++;
    }
    // 6. Tipo de organización
    totalFields++;
    if (profileData.basicInfo.organizationType && profileData.basicInfo.organizationType.toString().trim() !== '') {
      completedFields++;
    }
    // 7. Cargo
    totalFields++;
    if (profileData.contactInfo.position && profileData.contactInfo.position.toString().trim() !== '') {
      completedFields++;
    }
    // 8. Área
    totalFields++;
    if (profileData.contactInfo.area && profileData.contactInfo.area.toString().trim() !== '') {
      completedFields++;
    }
    // 9. Subárea
    totalFields++;
    if (profileData.contactInfo.subarea && profileData.contactInfo.subarea.toString().trim() !== '') {
      completedFields++;
    }
    // 10. Sitio web
    totalFields++;
    const website = profileData.basicInfo.website || '';
    if (website && website.toString().trim() !== '' && validateUrl(website.toString().trim())) {
      completedFields++;
    }
    // 11. Ejes de interés
    totalFields++;
    if (personalThematicAxesNormalized.length > 0) {
      completedFields++;
    }
    // Calcular porcentaje
    const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
    return {
      percentage: Math.min(percentage, 100),
      isComplete: percentage === 100
    };
  }
  // PERFIL INSTITUCIONAL: solo contar campos de Info de Organización y Modalidad de Participación
  // 1. Participación en el programa del evento
  totalFields++;
  const field1 = profileData.basicInfo.eventProgramParticipation && profileData.basicInfo.eventProgramParticipation.length > 0;
  if (field1) completedFields++;
  // 2. Logo
  totalFields++;
  const hasLogo = profileData.basicInfo.logo && 
    ((typeof profileData.basicInfo.logo === 'string' && profileData.basicInfo.logo.trim() !== '') ||
     (profileData.basicInfo.logo instanceof File));
  if (hasLogo) completedFields++;
  // 3. País
  totalFields++;
  const field3 = profileData.basicInfo.country && profileData.basicInfo.country.toString().trim() !== '';
  if (field3) completedFields++;
  // 4. Ejes Temáticos de la organización
  totalFields++;
  if (orgThematicAxesNormalized.length > 0) {
    completedFields++;
  }
  // 5. Descripción
  totalFields++;
  const field5 = profileData.institutionalDescription.description && 
      profileData.institutionalDescription.description.toString().trim() !== '';
  if (field5) completedFields++;
  // 6. Modalidades de participación seleccionadas
  totalFields++;
  const field6 = profileData.participationModalities && profileData.participationModalities.length > 0;
  if (field6) completedFields++;
  // Calcular porcentaje
  const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  return {
    percentage: Math.min(percentage, 100),
    isComplete: percentage === 100
  };
};
